import FormField from 'components/FormField';
import {
  Store,
  StoreScopesModal,
  StoreScopesPreview,
  getStoreCount,
  useStoreMap,
} from 'components/StoreScopes';
import { FV } from './share';
import { useFormikContext } from 'formik';
import { ReactNode, useCallback, useEffect, useState } from 'react';
import { Preview, storeScopes2stores, stores2storeScopes } from '../share';
import { Button } from 'antd';
import { EditOutlined, PlusOutlined } from '@ant-design/icons';
import { PreviewWrapper } from 'views/Assistant/Department/components/AssignReceptionConfColumn';
import { useTranslate } from '@tolgee/react';
import { useHelpContext } from '../HelpContext';
import { InlineHelp } from '../Help';

export function useGetStoresLabel() {
  const { t } = useTranslate();

  return {
    t,
    getStoresLabel: useCallback(
      (activityType: Knowledge.Activity.Type) => {
        return activityType === 'NEW_PRODUCT_ONSALE_ACTIVITY'
          ? t('新品上架店铺')
          : t('act_field_store');
      },
      [t],
    ),
  };
}

export interface StoresProps {
  readonly?: boolean;
  storeList?: Store[];
  storeMap?: Partial<Record<number, Store>>;
}

function Stores(props: StoresProps) {
  const { readonly, storeList, storeMap: storeMapProp } = props;

  const { t, getStoresLabel } = useGetStoresLabel();

  const { values } = useFormikContext<FV>();

  const { stores, activityType } = values;

  const label = getStoresLabel(activityType);

  const [storeScopes, setStoreScopes] = useState(() => stores2storeScopes(stores));

  const [visible, setVisible] = useState(false);

  const storeMap = useStoreMap({ list: storeList, map: storeMapProp });

  useEffect(() => {
    if (stores.length !== getStoreCount(storeScopes)) {
      setStoreScopes(stores2storeScopes(stores));
    }
  }, [storeScopes, stores]);

  const {
    stepState: [, setHelpStep],
  } = useHelpContext();

  return (
    <FormField<FV['stores']>
      name="stores"
      required
      label={label}
      validate={(value) => (!value.length ? t('department.请选择x', { x: label }) : undefined)}
    >
      {({ field: { value, onChange, onBlur } }) => {
        const withHelp = (...nodes: ReactNode[]) => (
          <InlineHelp step="适用店铺">
            <div className="inline-block min-w-[200px]">{nodes}</div>
          </InlineHelp>
        );

        const withPreview = (...nodes: ReactNode[]) =>
          withHelp(
            t('ViewBlockList.{x}家店铺', { count: value.length }),
            <Preview
              content={
                <PreviewWrapper>
                  <StoreScopesPreview
                    value={storeScopes}
                    showSearch={false}
                    showTitle={false}
                    storeMap={storeMap}
                  />
                </PreviewWrapper>
              }
            />,
            ...nodes,
          );

        if (readonly) {
          return withPreview();
        }

        const modal = (
          <StoreScopesModal
            visible={visible}
            singleRegion
            showFooterTips={false}
            storeDataSource={storeList}
            storeMap={storeMap}
            onCancel={() => {
              setVisible(false);
              onBlur();
            }}
            value={storeScopes}
            onChange={(storeScopes) => {
              setStoreScopes(storeScopes);
              onChange(storeScopes2stores(storeScopes, storeList, storeMap));
            }}
          />
        );

        const onSelect = () => {
          setVisible(true);
          setHelpStep('适用店铺');
        };

        if (!value.length) {
          return [
            modal,
            withHelp(
              <Button
                style={{ paddingLeft: 0 }}
                type="link"
                icon={<PlusOutlined />}
                onClick={onSelect}
              >
                {t('活动机器人-选择店铺')}
              </Button>,
            ),
          ];
        }

        return [
          modal,
          withPreview(<Button type="link" icon={<EditOutlined />} onClick={onSelect} />),
        ];
      }}
    </FormField>
  );
}

export default Stores;
